import { React, useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import UserContext from '../../../components/User_Context.js';
import * as menu from "../../../assets/menus/menu.js";
import * as api from "../../../api/apiConnector.js";
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { formBuilderLabels } from '../../_label/form_builder/form_builder_labels.js';
import * as breadcrumb from "../../../components/ui/breadcrumb.js";
import Form_Compiler from './compilerNew.js';

function ACPM_Finder() {
    const { currentUser } = useContext(UserContext);
    const { formId: urlFormId } = useParams();

    // UI state variables
    const [compilationsData, setCompilationsData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(null);
    const [selectedTableCell, setSelectedTableCell] = useState(null);
    const [showTableDetailModal, setShowTableDetailModal] = useState(false);
    const [loadingTableData, setLoadingTableData] = useState(false);
    
    // Form viewing state
    const [viewingForm, setViewingForm] = useState(null);
    const [viewingFormData, setViewingFormData] = useState(null);
    
    // Extract data for easier access
    const formInfo = compilationsData?.form || null;
    const headers = compilationsData?.headers || [];
    const rows = compilationsData?.rows || [];

    const { t } = useTranslation(); // Translation hook
    const MySwal = withReactContent(Swal); // SweetAlert instance

    // Set breadcrumb using centralized component
    const setBreadCrumb = () => {
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/acpm/finder", name: t('acpmFinder', { ns: 'labels' }) }
        ];
        breadcrumb.set(path);
    };

    // Initialize translations and set up component
    useEffect(() => {
        (async () => {
            // First initialize translations
            const i18n = menu.getI18n();
            i18n.addResourceBundle('it', 'labels', formBuilderLabels.it, true, true);
            i18n.addResourceBundle('en', 'labels', formBuilderLabels.en, true, true);
            
            // Then set breadcrumb with translations available
            setBreadCrumb();
            menu.write({ user: currentUser() });

            // Automatically load ACPM forms on component mount
            loadACPMForms();
        })();
    }, []);

    // Fetch ACPM compilations table data from API
    const loadACPMForms = async () => {
        setLoading(true);
        try {
            // Get formId from URL params or env variable - no hardcoded fallback
            const formId = urlFormId || process.env.REACT_APP_DEFAULT_APCM;

            const result = await api.sendMessage("darm/formBuilder/getCompilationsTable", {
                user: currentUser(),
                body: {
                    formId: formId
                }
            });

            if (result.success) {
                setCompilationsData(result.data);
                console.log("Compilations data loaded:", result.data);
            } else {
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: result.error || t('errorLoadingACPMForms', { ns: 'labels' }),
                    icon: 'error'
                });
                setCompilationsData(null);
            }
        } catch (error) {
            console.error("Error loading ACPM compilations:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: t('errorLoadingACPMForms', { ns: 'labels' }),
                icon: 'error'
            });
            setCompilationsData(null);
        } finally {
            setLoading(false);
            setRefresh(new Date()); // Trigger UI update
        }
    };

    // Format ISO date/datetime string to dd/mm/yyyy
    const formatDate = (isoString) => {
        if (!isoString) return "";
        try {
            const date = new Date(isoString);
            if (isNaN(date.getTime())) return isoString; // Return original if not valid date
            return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
        } catch (error) {
            console.error("Date formatting error:", error);
            return isoString; // Return original string if error
        }
    };


    // Check if a cell is of type table (for modal interaction)
    const isCellTableType = (cell) => {
        return cell && cell.type === 'table';
    };

    // Check if a header represents a table-type question
    const isHeaderTableType = (header) => {
        return header && header.questionType === 'table';
    };

    // Handle cell click for table type cells (opens modal with table data)
    const handleTableCellClick = (cell, header, row) => {
        if (isCellTableType(cell)) {
            console.log('Opening modal for table cell:', header.text);
            setSelectedTableCell({
                cell,
                header,
                row,
                tableData: cell.data || []
            });
            setShowTableDetailModal(true);
        }
    };

    // Close table detail modal
    const closeTableDetailModal = () => {
        setShowTableDetailModal(false);
        setSelectedTableCell(null);
        setLoadingTableData(false);
    };

    // Handle viewing form in read-only mode
    const handleViewFormDetails = async (row) => {
        try {
            // Show loading indicator
            MySwal.fire({
                title: t('loading', { ns: 'labels' }),
                text: t('loadingFormData', { ns: 'labels', defaultValue: 'Loading form data...' }),
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });

            // Load both the form completion data AND the form structure
            const [completionResult, formResult] = await Promise.all([
                // Load the form completion data
                api.sendMessage("darm/formCompletion/get", {
                    user: currentUser(),
                    body: {
                        formId: formInfo?.id,
                        completionId: row.completionId
                    }
                }),
                // Load the form structure
                api.sendMessage("darm/formBuilder/get", {
                    user: currentUser(),
                    body: {
                        id: formInfo?.id
                    }
                })
            ]);

            MySwal.close();

            if (completionResult?.success && completionResult?.formCompletion && formResult?.success && formResult?.form) {
                // Combine the form structure with the completion data
                const combinedData = {
                    ...completionResult.formCompletion,
                    form: formResult.form
                };
                
                console.log("🔍 [DEBUG] Combined form data:", combinedData);
                
                // Set the viewing form data
                setViewingForm(row);
                setViewingFormData(combinedData);
                
                // Update breadcrumb when viewing a form
                updateBreadcrumbForFormView(formInfo?.name || 'Form');
            } else {
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: completionResult?.message || formResult?.message || t('failedToLoadForm', { ns: 'labels', defaultValue: 'Failed to load form data' }),
                    icon: 'error'
                });
            }
        } catch (error) {
            MySwal.close();
            console.error("Error loading form for viewing:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('failedToLoadForm', { ns: 'labels', defaultValue: 'Failed to load form data' }),
                icon: 'error'
            });
        }
    };

    // Handle closing the form viewer
    const handleCloseFormViewer = () => {
        setViewingForm(null);
        setViewingFormData(null);
        // Reset breadcrumb to dashboard
        setBreadCrumb();
    };

    // Update breadcrumb when viewing a form
    const updateBreadcrumbForFormView = (formName) => {
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/acpm/finder", name: t('acpmFinder', { ns: 'labels' }) },
            { link: "#", name: `${t('viewingForm', { ns: 'labels', defaultValue: 'Visualizzazione Form' })}: ${formName}` }
        ];
        breadcrumb.set(path);
    };

    // These functions are now moved inside the modal component for consistency with FormQuestion
    // They are defined within renderTableDetailModal()

    // Render a cell in the compilations table
    const renderTableCell = (cell, header, row, isLastColumn = false) => {
        if (!cell || cell.type === 'empty') {
            return (
                <td 
                    key={header.id} 
                    style={{ 
                        padding: '10px 12px',
                        maxWidth: '150px',
                        fontSize: '0.875rem',
                        ...(isLastColumn ? {
                            borderTopRightRadius: '6px',
                            borderBottomRightRadius: '6px'
                        } : {})
                    }}
                >
                    <span className="text-muted">-</span>
                </td>
            );
        }

        const isTableCell = isCellTableType(cell);
        
        return (
            <td
                key={header.id}
                onClick={(e) => {
                    e.stopPropagation();
                    if (isTableCell) {
                        handleTableCellClick(cell, header, row);
                    }
                }}
                style={{ 
                    padding: '10px 12px',
                    cursor: isTableCell ? 'pointer' : 'default',
                    maxWidth: '150px',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    fontSize: '0.875rem',
                    ...(isTableCell ? { 
                        backgroundColor: '#f8fff8',
                        color: '#0d6efd'
                    } : {}),
                    ...(isLastColumn ? {
                        borderTopRightRadius: '6px',
                        borderBottomRightRadius: '6px'
                    } : {})
                }}
                className={isTableCell ? 'table-cell-clickable' : ''}
                title={isTableCell ? (t('clickToViewTableDetails', { ns: 'labels' }) !== 'clickToViewTableDetails' ? t('clickToViewTableDetails', { ns: 'labels' }) : 'Clicca per visualizzare i dettagli della tabella') : ''}
            >
                {isTableCell ? (
                    <div className="d-flex align-items-center">
                        <i className="fa-solid fa-table text-primary me-2" style={{ fontSize: '0.75rem' }} />
                        <span>{cell.displayText || '0 record'}</span>
                    </div>
                ) : (
                    <span>{cell.displayText || '-'}</span>
                )}
            </td>
        );
    };

    // Render a row in the compilations table
    const getTableRow = (row) => {
        return (
            <tr 
                key={row.completionId} 
                style={{ 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '6px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    cursor: 'pointer'
                }}
                onDoubleClick={() => handleViewFormDetails(row)}
                title={t('doubleClickToView', { ns: 'labels', defaultValue: 'Doppio click per visualizzare i dettagli' })}
            >
                {/* Compiler column */}
                <td style={{ 
                    padding: '10px 12px', 
                    borderTopLeftRadius: '6px', 
                    borderBottomLeftRadius: '6px',
                    maxWidth: '220px',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    fontSize: '0.875rem'
                }}>
                    <div className="d-flex align-items-center">
                        <i className="fa-solid fa-user text-muted me-2"></i>
                        <span>{row.compiler?.name || '-'}</span>
                    </div>
                </td>

                {/* Status column */}
                <td style={{ 
                    padding: '10px 12px',
                    maxWidth: '100px'
                }}>
                    <span className={`badge ${getStatusBadgeClass(row.status)}`} style={{ fontSize: '0.75rem' }}>
                        {(() => {
                            const statusKey = row.status?.toLowerCase() || 'unknown';
                            const translated = t(statusKey, { ns: 'labels' });
                            if (translated !== statusKey) return translated;
                            
                            // Fallback translations
                            switch (row.status) {
                                case 'Assigned': return 'Assegnato';
                                case 'Saved': return 'Salvato';
                                case 'Compiled': return 'Compilato';
                                case 'Approved': return 'Approvato';
                                default: return row.status || 'Sconosciuto';
                            }
                        })()}
                    </span>
                </td>

                {/* Date column */}
                <td style={{ 
                    padding: '10px 12px',
                    maxWidth: '120px',
                    fontSize: '0.875rem'
                }}>
                    <div className="d-flex align-items-center text-muted">
                        <i className="fa-solid fa-calendar me-2"></i>
                        <span>{row.created ? formatDate(row.created) : '-'}</span>
                    </div>
                </td>

                {/* Dynamic columns from headers (form questions) */}
                {headers.map((header, index) => {
                    const isLastColumn = index === headers.length - 1;
                    return renderTableCell(row.cells[header.id], header, row, isLastColumn);
                })}

            </tr>
        );
    };

    // Helper function to get CSS class for status badges
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'Assigned': return 'bg-warning';
            case 'Saved': return 'bg-info';
            case 'Compiled': return 'bg-success';
            case 'Approved': return 'bg-primary';
            default: return 'bg-secondary';
        }
    };

    // Render table detail modal for table-type cells
    const renderTableDetailModal = () => {
        if (!showTableDetailModal || !selectedTableCell) return null;

        // Get column configuration from the selected cell header
        const getTableColumns = () => {
            // Use the table columns from the header definition
            return selectedTableCell.header.tableColumns || [];
        };

        const columns = getTableColumns();
        const tableSettings = {
            showRowNumbers: true,
            allowAddRows: false,    // Read-only mode
            allowDeleteRows: false, // Read-only mode
            minRows: 0,
            maxRows: 999
        };

        // Helper function to get localized column name
        const getColumnName = (column) => {
            const nameText = column.name?.find(n => n.language === 'it')?.text;
            return nameText || `Column ${columns.indexOf(column) + 1}`;
        };

        // Read-only modal - no editing functions needed

        // Helper function to get localized text
        const getLocalizedText = (textArray, fallback) => {
            if (Array.isArray(textArray)) {
                const text = textArray.find(t => t.language === 'it')?.text;
                return text || fallback;
            }
            return textArray || fallback;
        };

        // Render table cell in read-only mode
        const renderModalTableCell = (row, column, rowIndex) => {
            const cellValue = row[column.id] || '';
            
            // Format the value for display
            let displayValue = cellValue;
            
            if (column.type === 'date' && cellValue) {
                // Format date for display
                displayValue = formatDate(cellValue);
            } else if (column.type === 'select' && cellValue) {
                // Show the selected option label
                const selectedOption = column.options?.find(opt => opt.value === cellValue);
                displayValue = selectedOption ? getLocalizedText(selectedOption.text, cellValue) : cellValue;
            }
            
            return (
                <span className="text-break">
                    {displayValue || '-'}
                </span>
            );
        };

        return (
            <div 
                className="modal show d-block" 
                tabIndex="-1" 
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            >
                <div className="modal-dialog modal-xxl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <div>
                                <h4 className="modal-title mb-1">
                                    {selectedTableCell.header.text} - {formInfo?.name || 'Form'}
                                </h4>
                                <small className="text-muted">
                                    {t('compiler', { ns: 'labels' }) !== 'compiler' ? t('compiler', { ns: 'labels' }) : 'Compilatore'}: {selectedTableCell.row.compiler?.name} | 
                                    <span className="ms-2 badge bg-info">{t('readOnlyMode', { ns: 'labels' }) !== 'readOnlyMode' ? t('readOnlyMode', { ns: 'labels' }) : 'Modalità Sola Lettura'}</span>
                                </small>
                            </div>
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={closeTableDetailModal}
                            ></button>
                        </div>
                        
                        <div className="modal-body">
                            {loadingTableData ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">{t('loadingTableData', { ns: 'labels' }) !== 'loadingTableData' ? t('loadingTableData', { ns: 'labels' }) : 'Caricamento dati tabella...'}</p>
                                </div>
                            ) : (
                                <div className="table-responsive mt-3">
                                    <table className="table table-bordered table-sm">
                                        <thead>
                                            <tr>
                                                {tableSettings.showRowNumbers && (
                                                    <th style={{ width: '60px' }}>N.</th>
                                                )}
                                                {columns.map((column) => (
                                                    <th key={column.id}>
                                                        {getColumnName(column)}
                                                        {column.required && <span className="text-danger ms-1">*</span>}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!selectedTableCell.tableData || selectedTableCell.tableData.length === 0 ? (
                                                <tr>
                                                    <td colSpan={columns.length + (tableSettings.showRowNumbers ? 1 : 0)} className="text-center text-muted py-4">
                                                        <div>
                                                            <i className="fa fa-table me-2"></i>
                                                            {t('tableEmpty', { ns: 'labels' }) !== 'tableEmpty' ? t('tableEmpty', { ns: 'labels' }) : 'Tabella vuota'}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                selectedTableCell.tableData.map((row, rowIndex) => (
                                                    <tr key={rowIndex}>
                                                        {tableSettings.showRowNumbers && (
                                                            <td className="text-center">{rowIndex + 1}</td>
                                                        )}
                                                        {columns.map((column) => (
                                                            <td key={column.id}>
                                                                {renderModalTableCell(row, column, rowIndex)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-primary" 
                                onClick={closeTableDetailModal}
                            >
                                <i className="fa-solid fa-times me-1"></i>
                                {t('close', { ns: 'labels' }) !== 'close' ? t('close', { ns: 'labels' }) : 'Chiudi'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render the full compilations table
    const getTable = () => (
        <div style={{ overflowX: 'auto', width: '100%' }}>
            <table className="table table-hover" style={{ minWidth: '900px', borderCollapse: 'separate', borderSpacing: '0 6px' }}>
                <thead>
                    <tr className="text-muted" style={{ fontSize: '0.75rem' }}>
                        <th style={{ 
                            padding: '8px 12px', 
                            minWidth: '180px',
                            maxWidth: '220px',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: '1.2'
                        }}>
                            {t('compiler', { ns: 'labels' }) !== 'compiler' ? t('compiler', { ns: 'labels' }) : 'Compilatore'}
                        </th>
                        <th style={{ 
                            padding: '8px 12px', 
                            minWidth: '80px',
                            maxWidth: '100px',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: '1.2'
                        }}>
                            {t('status', { ns: 'labels' }) !== 'status' ? t('status', { ns: 'labels' }) : 'Stato'}
                        </th>
                        <th style={{ 
                            padding: '8px 12px', 
                            minWidth: '90px',
                            maxWidth: '120px',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: '1.2'
                        }}>
                            {t('created', { ns: 'labels' }) !== 'created' ? t('created', { ns: 'labels' }) : 'Data Creazione'}
                        </th>
                        {/* Dynamic column headers from form questions */}
                        {headers.map((header) => (
                            <th
                                key={header.id}
                                style={{
                                    padding: '8px 12px',
                                    minWidth: '100px',
                                    maxWidth: '150px',
                                    wordWrap: 'break-word',
                                    whiteSpace: 'normal',
                                    lineHeight: '1.2'
                                }}
                            >
                                {header.text}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(getTableRow)}
                </tbody>
            </table>
        </div>
    );

    // If viewing a form, show the Form_Compiler in read-only mode
    if (viewingForm && viewingFormData) {
        return (
            <div className="container-fluid">
                <Form_Compiler
                    form={viewingFormData.form}
                    formCompletion={viewingFormData}
                    language="it"
                    readOnly={true}
                    onClose={handleCloseFormViewer}
                    colors={{
                        primary: '#f05f23',
                        primaryHover: '#d44d12',
                        primaryLight: '#fef0ea',
                        secondary: '#6c757d',
                        secondaryLight: '#e9ecef',
                        dark: '#343a40',
                        light: '#f8f9fa',
                        white: '#ffffff',
                        success: '#28a745',
                        danger: '#dc3545',
                        warning: '#ffc107',
                        info: '#17a2b8'
                    }}
                />
            </div>
        );
    }

    return (
        <>
            {/* Breadcrumb managed by centralized component */}
            <div className="page-titles">
                <ol className="breadcrumb">
                </ol>
            </div>
            
            {/* Table Detail Modal */}
            {renderTableDetailModal()}
            
            {/* CSS for clickable table cells and larger modal */}
            <style>
                {`
                .table-cell-clickable:hover {
                    background-color: #e6f3ff !important;
                    transition: background-color 0.2s ease;
                }
                .table-cell-clickable {
                    cursor: pointer;
                }
                .modal-xxl {
                    max-width: 85vw !important;
                    margin: 1.5rem auto !important;
                }
                .modal-xxl .modal-content {
                    height: 85vh !important;
                    max-height: 85vh !important;
                }
                .modal-xxl .modal-body {
                    overflow-y: auto;
                    max-height: calc(85vh - 120px);
                }
                .table thead th {
                    vertical-align: top !important;
                    font-weight: 600 !important;
                }
                .table tbody tr:hover {
                    background-color: rgba(0, 123, 255, 0.1) !important;
                    transition: background-color 0.15s ease;
                }
                .table tbody tr {
                    transition: background-color 0.15s ease;
                }
                `}
            </style>

            <div className="container-fluid">
                {/* Dashboard header card */}
                <div className="card mb-4" style={{ border: '1px solid #dee2e6', borderRadius: '8px' }}>
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h4 className="card-title mb-1">
                                    {formInfo ? formInfo.name : t('acpmFinder', { ns: 'labels' })}
                                </h4>
                                <p className="mb-0 text-muted">
                                    {formInfo ? `${formInfo.code} - ${t('compilations', { ns: 'labels' }) !== 'compilations' ? t('compilations', { ns: 'labels' }) : 'Compilazioni'} disponibili per la consultazione` : t('acpmFinder', { ns: 'labels' })}
                                </p>
                            </div>
                            {rows && (
                                <h5 className="mb-0">
                                    {t('totalCompilations', { ns: 'labels', defaultValue: 'Totale' })}
                                    {rows.length > 0 && (
                                        <span className="badge bg-primary ms-2">{rows.length}</span>
                                    )}
                                </h5>
                            )}
                        </div>
                    </div>
                </div>

                {/* Compilations table card */}
                <div className="card" style={{ borderRadius: '8px' }}>
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-semibold">
                            {t('compilationsList', { ns: 'labels' }) !== 'compilationsList' ? t('compilationsList', { ns: 'labels' }) : 'Elenco Compilazioni'} 
                            ({rows ? rows.length : 0})
                        </h5>
                    </div>
                    <div className="card-body p-0">
                        <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {rows && rows.length > 0 ? (
                                <div className="w-100">
                                    {getTable()}
                                </div>
                            ) : (
                                <div className="text-muted text-center py-5" style={{ fontSize: '1.1rem' }}>
                                    <i className="fa-solid fa-inbox fa-3x mb-3 opacity-50"></i>
                                    <div className="fw-medium">
                                        {loading ? 
                                            t('loadingACPMForms', { ns: 'labels' }) : 
                                            (t('noCompilationsFound', { ns: 'labels' }) !== 'noCompilationsFound' ? t('noCompilationsFound', { ns: 'labels' }) : 'Nessuna compilazione trovata')
                                        }
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export { ACPM_Finder };
